import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

const SIDEBAR_STORAGE_KEY = 'app.sidebarCollapsed';
const THEME_STORAGE_KEY = 'app.theme';

export type ThemeMode = 'light' | 'dark';
export type MobileSidebarView = 'main' | 'settings';

export interface UIState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  mobileSidebarView: MobileSidebarView;
  hasUnsavedSettingsChanges: boolean;
  theme: ThemeMode;
}

function readCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return raw ? JSON.parse(raw) === true : false;
  } catch {
    return false;
  }
}

function readTheme(): ThemeMode {
  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return storedTheme === 'dark' || storedTheme === 'light' ? storedTheme : 'light';
  } catch {
    return 'light';
  }
}

const initialState: UIState = {
  isCollapsed: readCollapsed(),
  isMobileOpen: false,
  mobileSidebarView: 'main',
  hasUnsavedSettingsChanges: false,
  theme: readTheme(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleCollapse(state) {
      state.isCollapsed = !state.isCollapsed;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(state.isCollapsed));
      } catch {
        // ignore
      }
    },
    setCollapsed(state, action: PayloadAction<boolean>) {
      state.isCollapsed = !!action.payload;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(state.isCollapsed));
      } catch {
        // ignore
      }
    },
    openMobile(state) {
      state.isMobileOpen = true;
      state.mobileSidebarView = 'main';
    },
    openMobileSettings(state) {
      state.isMobileOpen = true;
      state.mobileSidebarView = 'settings';
    },
    showMobileMain(state) {
      state.mobileSidebarView = 'main';
    },
    closeMobile(state) {
      state.isMobileOpen = false;
    },
    setSettingsUnsavedChanges(state, action: PayloadAction<boolean>) {
      state.hasUnsavedSettingsChanges = action.payload;
    },
    toggleTheme(state) {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, state.theme);
      } catch {
        // ignore
      }
    },
    setTheme(state, action: PayloadAction<ThemeMode>) {
      state.theme = action.payload;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, state.theme);
      } catch {
        // ignore
      }
    },
  },
});

export const uiReducer = uiSlice.reducer;
export const uiActions = uiSlice.actions;
export default uiReducer;
